import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import {
  DEFAULT_DAILY_LIMIT,
  isSendDay,
  outsideBusinessHours,
  scheduleChannels,
  selectedTimezone,
  TIMEZONES,
  type TimezoneOption,
  withSendDayToggled,
} from './logic/schedule.js';
import type { ScheduleConfig, SequenceStep, Weekday } from './types.js';
import { WEEKDAYS } from './types.js';

export interface WizScheduleProps {
  schedule: ScheduleConfig;
  sequence: SequenceStep[];
  onScheduleChange: (schedule: ScheduleConfig) => void;
}

export const WizSchedule = stateful((props: WizScheduleProps) => {
  const { schedule, sequence, onScheduleChange } = props;
  // The schedule step adapts itself to whichever channels the sequence uses.
  // • If LinkedIn is part of the sequence, we show the LinkedIn-specific
  //   business-hours warning and the fixed daily-limit notice.
  // • If only LinkedIn is used, the daily-limit input is hidden entirely.
  const { hasLinkedin, linkedinOnly } = scheduleChannels(sequence);

  const update = (patch: Partial<ScheduleConfig>) => {
    onScheduleChange({ ...schedule, ...patch });
  };

  const toggleDay = (weekday: Weekday) => {
    update({ sendDays: withSendDayToggled(schedule, weekday).sendDays });
  };

  const selectedTz = selectedTimezone(schedule.timezone);

  return html`
    <div class="flex flex-col gap-6">
      <!-- Sending Window -->
      <div class="card bg-base-100">
        <div class="card-body flex flex-col gap-5 p-6">
          <div class="flex items-center gap-3">
            <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--schedule] text-xl"></span>
            </span>
            <div>
              <h2 class="text-[0.9375rem] font-medium">Schedule & timing</h2>
              <p class="text-base-content/70 text-sm">
                Configure when messages are sent, the timezone, and daily limits.
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium">Timezone</span>
            ${DropdownSelectionInput<TimezoneOption>({
              value: selectedTz,
              selectables: TIMEZONES,
              renderString: tz => tz.label,
              resolveID: tz => tz.value,
              onPicked: tz => {
                if (tz) update({ timezone: tz.value });
              },
              placeholder: 'Select timezone…',
            })}
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">Start time</span>
              ${DateInput({
                type: 'time',
                value: schedule.startTime,
                onPicked: d => {
                  if (d) update({ startTime: d });
                },
              })}
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">End time</span>
              ${DateInput({
                type: 'time',
                value: schedule.endTime,
                onPicked: d => {
                  if (d) update({ endTime: d });
                },
              })}
            </div>
          </div>

          ${
            hasLinkedin &&
            (
              () => {
                return outsideBusinessHours(schedule)
                  ? html`
              <div class="alert alert-warning py-2 text-sm">
                <span class="icon-[material-symbols--warning-outline] text-base"></span>
                <span>Sending outside business hours (9–17) can increase the chance of your LinkedIn sender getting blocked.</span>
              </div>
            `
                  : null;
              }
            )()
          }

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium">Send days</span>
            <div class="flex flex-wrap gap-2">
              ${WEEKDAYS.map(
                weekday => html`
                  <!-- A plain chip, not .btn: the theme's secondary-button rule would cover the selected tint. -->
                  <button
                    aria-pressed=${isSendDay(schedule, weekday) ? 'true' : 'false'}
                    class="rounded-field border px-3 py-1.5 text-sm transition-colors ${
                      isSendDay(schedule, weekday)
                        ? 'border-primary bg-(--sdr-selected) text-base-content font-medium'
                        : 'border-base-300 hover:border-(--sdr-line-strong) text-base-content/70'
                    }"
                    @click=${() => toggleDay(weekday)}
                  >
                    ${weekday.displayName}
                  </button>
                `,
              )}
            </div>
          </div>

          ${
            linkedinOnly
              ? html`
            <div class="alert alert-info py-2 text-sm">
              <span class="icon-[material-symbols--info-outline] text-base"></span>
              <span>Daily limit set to 20 connection requests due to LinkedIn restrictions.</span>
            </div>
          `
              : html`
            <div class="flex flex-col gap-1" style="max-width: 200px">
              <span class="text-sm font-medium">Daily limit</span>
              ${NumberInput({
                value: schedule.dailyLimit,
                onPicked: value => update({ dailyLimit: value ?? DEFAULT_DAILY_LIMIT }),
                min: 1,
                max: 500,
                placeholder: '30',
              })}
            </div>
          `
          }
        </div>
      </div>
    </div>
  `;
});
